import { textColorFields } from '../_shared/textColor.js';
import { animationFields } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
import { patternFields } from '../_shared/pattern.js';
/**
 * track-order — a FROM-SCRATCH, 1:1 rebuild of Shrine Pro's "Track order" section
 * (shrine-pro-ref/sections/track-order.liquid). It is the order-tracking band: a highlightable HEADING over a
 * paragraph over a single floating-label TEXT input (`.field` → `.field__input` + `.field__label`) and a
 * TRACK button, wired to the 17TRACK widget (`//www.17track.net/externalcall.js` → `YQV5.trackSingle`), whose
 * result renders into `#YQContainer` — exactly as Shrine ships it. The colored band flips palette via the 6
 * Shrine color schemes (+ a full custom palette) and can carry the shared 13-pattern background.
 *
 * The whole config is preserved (same knobs, same defaults, same order): display_id + visibility chrome, the
 * inline_richtext heading with BOTH word-HIGHLIGHT engines (title_highlight_1 / _2 + every sub-knob) and the
 * h3/h2/h1/h0/custom heading-size scale, the richtext text, the input label, the button label + full-width
 * toggle, the color_scheme + 7 custom-palette overlays (bg / gradient / image / mobile image / text / solid
 * button bg / solid button label), the pattern_bg engine, and the section padding (Shrine default 36 / 36).
 *
 * Field-type mapping (Shopify → ours, same convention as the newsletter / contact-form ports):
 * inline_richtext/richtext → richtext (bold → highlight-1, italic → highlight-2), text → text, select →
 * select, checkbox → checkbox, color → color, color_background → gradient, image_picker → image, range →
 * range, header → group, visible_if → visibleWhen. Ids are clean camelCase; labels / options / defaults stay
 * faithful to Shrine.
 *
 * DELIBERATELY DEFERRED (faithful follow-ups, flagged): (a) 17TRACK is a THIRD-PARTY widget — the storefront
 * loads `//www.17track.net/externalcall.js` on first TRACK click, so preprod/prod Caddy CSP must allowlist
 * `www.17track.net` (same class of change as the js.stripe.com CSP fix) for the result to render live; (b) the
 * compound `visible_if` on customMobileImageBackground — Shrine gates it on `color_scheme == custom AND
 * custom_image_background != blank`, but our single-field VisibleWhen DSL can only express the `custom` half,
 * so the mobile-image field shows a touch early (same DSL limit as newsletter / collapsible-content; the
 * Component's mobile-image CSS no-ops when no desktop image is set, so it's inspector-only).
 */
/** Convenience for the literal hex defaults Shrine ships. */
const hex = (value) => ({ ref: 'custom', value });
const HEADING = 'Heading';
const CONTENT = 'Content';
const COLOR = 'Color';
export const trackOrderSchema = {
    type: 'track-order',
    name: 'Track order',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── (top, ungrouped) — Shopify section chrome ─────────────────────────────────
        {
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            default: false,
            info: 'Lets a button link scroll to this section.',
        },
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        // ── Heading: the highlightable title + BOTH highlight engines + size ──────────
        {
            id: 'title',
            type: 'richtext',
            label: 'Heading',
            group: HEADING,
            default: { format: 'html', html: '<p>Track your order</p>' },
            info: 'Bold (B) a word for highlight 1, Italic (I) a word for highlight 2.',
        },
        ...headingHighlightFields(1, HEADING),
        ...headingHighlightFields(2, HEADING),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: HEADING,
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customDesktopSize',
            type: 'range',
            label: 'Custom desktop size',
            group: HEADING,
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'customMobileSize',
            type: 'range',
            label: 'Custom mobile size',
            group: HEADING,
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        // ── Content: paragraph + input + button ──────────────────────────────────────
        {
            id: 'text',
            type: 'richtext',
            label: 'Text',
            group: CONTENT,
            default: {
                format: 'html',
                html: "<p>Please enter you tracking number below. If your tracking number doesn't work feel free to contact us at [shop email]</p>",
            },
        },
        {
            id: 'inputLabel',
            type: 'text',
            label: 'Input label',
            group: CONTENT,
            default: 'Tracking number',
            defaultKey: 'section.tracking_number',
            maxLength: 50,
        },
        {
            id: 'buttonLabel',
            type: 'text',
            label: 'Button label',
            group: CONTENT,
            default: 'TRACK',
            maxLength: 40,
        },
        {
            id: 'buttonFullWidth',
            type: 'checkbox',
            label: 'Use full width button style',
            group: CONTENT,
            default: true,
        },
        // ── Color: the 6 Shrine schemes + a full custom palette (same engine as newsletter) ─
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: COLOR,
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customBackground',
            type: 'color',
            label: 'Custom background color',
            group: COLOR,
            default: hex('#FFFFFF'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customGradientBackground',
            type: 'gradient',
            label: 'Custom gradient background',
            group: COLOR,
            info: 'If set, replaces the background color.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customImageBackground',
            type: 'image',
            label: 'Custom image background',
            group: COLOR,
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customMobileImageBackground',
            type: 'image',
            label: 'Custom mobile image background',
            group: COLOR,
            info: 'If empty, the desktop image is used on mobile too.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customText',
            type: 'color',
            label: 'Custom text color',
            group: COLOR,
            default: hex('#2E2A39'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customButtonBackground',
            type: 'color',
            label: 'Custom solid button background',
            group: COLOR,
            default: hex('#dd1d1d'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customButtonText',
            type: 'color',
            label: 'Custom solid button label color',
            group: COLOR,
            default: hex('#ffffff'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        // ── Background pattern (shared 13-pattern engine — Shrine pattern_bg + sub-knobs) ─
        ...patternFields('Background pattern'),
        // ── Section padding (Shrine default 36 / 36) ─────────────────────────────────
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
};
export const trackOrderPresets = [
    {
        name: 'Track order',
        settings: {
            title: { format: 'html', html: '<p>Track your order</p>' },
            headingHighlight1: 'solid-color',
            headingHighlight1Color: { ref: 'theme', token: 'accent' },
            headingSize: 'h1',
            text: {
                format: 'html',
                html: "<p>Please enter you tracking number below. If your tracking number doesn't work feel free to contact us at [shop email]</p>",
            },
            buttonLabel: 'TRACK',
            buttonFullWidth: true,
            colorScheme: 'background-1',
            paddingTop: 36,
            paddingBottom: 36,
        },
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const trackOrderMeta = {
    label: 'Track order',
    group: 'Content',
    icon: 'search',
};
//# sourceMappingURL=schema.js.map